/**
 * Пульт диспетчера.
 *
 * Экран собирается по одной схеме: снапшот по REST даёт состояние и seq, дальше
 * приходят только дельты по веб-сокету (§8.1). Событие не применяется к данным вручную —
 * оно помечает снапшот устаревшим и вызывает перезапрос: один код на первую загрузку
 * и на восстановление после обрыва, расходиться нечему.
 *
 * Справочник меток в снапшот не входит: он общий для всех и живёт на GET /api/labels
 * со своим кэшем. Тянуть его в каждый снапшот значит гонять справочник на каждое
 * событие очереди.
 *
 * Композиция экрана: слева — кому звоним, в центре — звонок и его результат,
 * справа — остальные карточки. С 29.08.2026 метка результата обязательна:
 * пока по завершённому звонку она не проставлена, набор следующего номера
 * закрыт (features/desk/unlabeled.ts).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Eye, Phone } from 'lucide-react';
import type {
  DeskAssignment,
  DeskCallIntentBody,
  DeskCallIntentResponse,
  DeskCallbacksResponse,
  DeskCallbackTakeResponse,
  DeskLabelBody,
  DeskLabelResponse,
  DeskManualCallBody,
  DeskManualCallResponse,
  DeskReadyBody,
  DeskReadyResponse,
  DeskRequestContactsResponse,
  DeskSnapshotResponse,
  LabelsResponse,
  UserRow,
} from '@cc/shared';
import type { SoftphoneConfig, SoftphoneSnapshot } from '@cc/softphone-core';

import { api } from '../shared/api/client.js';
import { RealtimeClient } from '../shared/api/ws.js';
import { blockedReasonTitle } from '../shared/lib/blocked-reason.js';
import { errorText } from '../shared/lib/error-text.js';
import { ErrorNotice } from '../shared/ui/error-notice.js';
import { Button } from '../shared/ui/button.js';
import { useCurrentUser } from '../app/auth-guard.js';
import { SoftphoneProvider, useLineRepair } from '../features/softphone/provider.js';
import { useSoftphone } from '../features/softphone/use-softphone.js';
import { isManualCall, manualCallErrorText } from '../features/softphone/manual-dial.js';
import { DevicePicker } from '../features/softphone/ui/device-picker.js';
import { LineIndicator } from '../features/softphone/ui/line-indicator.js';
import { ManualDialForm } from '../features/softphone/ui/manual-dial-form.js';
import { formatPhone } from '../shared/lib/format-phone.js';
import { formatTimeMsk } from '../shared/lib/msk.js';
import { CallPanel } from '../features/desk/call-panel.js';
import { ContactCard } from '../features/desk/contact-card.js';
import { DayStats } from '../features/desk/day-stats.js';
import { LabelPicker } from '../features/desk/label-picker.js';
import { CallbacksPanel } from '../features/desk/callbacks-panel.js';
import { PHONE_LOOKUP_KEY, PhoneLookup } from '../features/desk/phone-lookup.js';
import { MyQueue } from '../features/desk/my-queue.js';
import { NeedsLabel } from '../features/desk/needs-label.js';
import { AudioCheck } from '../features/desk/audio-check.js';
import { ReadyToggle } from '../features/desk/ready-toggle.js';
import {
  dialAllowed,
  labelTargetFor,
  orderForDesk,
  unlabeledAssignments,
} from '../features/desk/unlabeled.js';
import { browserDraftStorage, clearLabelDraft, purgeLabelDrafts } from '../features/desk/label-draft.js';
import { playConnectBeep } from '../features/desk/connect-beep.js';
import { applyVolume, readVolume, volumeForPhase, writeVolume, type CallVolume } from '../features/softphone/volume.js';

const SNAPSHOT_KEY = ['desk', 'snapshot'] as const;
const LABELS_KEY = ['desk', 'labels'] as const;

/** Выбор диспетчера «набирать самому после метки» — переживает F5 */
const AUTODIAL_STORAGE_KEY = 'cc.desk.autodial';
/**
 * Пауза перед автонабором. Ноль — набор идёт сразу после метки: диспетчеры
 * просили убрать отсчёт «наберу через 3…», он только тормозил (06.09.2026).
 * Механизм взвода остаётся: набор стартует только после метки и только пока
 * условия набора выполняются; остановить — тумблером или отбоем.
 */
const AUTODIAL_DELAY_MS = 0;
/**
 * Взведённый автонабор протухает, если набор так и не открылся: карточки
 * кончились или линия упала. Звонок, стартующий сам спустя минуты тишины, —
 * это звонок, которого диспетчер уже не ждёт.
 */
const AUTODIAL_STALE_MS = 15_000;

const deskRouteApi = getRouteApi('/desk');

export function DeskPage(): React.JSX.Element {
  const user = useCurrentUser();
  const { dispatcher } = deskRouteApi.useSearch();

  /**
   * Наблюдение: админ и супервизор открывают пульт глазами диспетчера
   * (?dispatcher=<id>). Смотреть и ставить метки можно, звонить нельзя —
   * линия живёт в браузере владельца. Диспетчеру чужой пульт закрыт и здесь,
   * и на сервере.
   */
  const observedId =
    user.role !== 'dispatcher' && dispatcher !== undefined && dispatcher !== user.id
      ? dispatcher
      : null;

  // Параметры SIP выдаёт сервер по сессии: пароль эндпоинта в коде не хранится.
  // В наблюдении свой софтфон не поднимается вовсе — этот пульт не звонит
  const sipQuery = useQuery({
    queryKey: ['desk', 'sip-config', user.id],
    queryFn: () => api.get<SoftphoneConfig>('/desk/softphone-config'),
    staleTime: Infinity,
    enabled: observedId === null,
  });

  return (
    <SoftphoneProvider config={observedId === null ? (sipQuery.data ?? null) : null}>
      <DeskScreen observedId={observedId} />
    </SoftphoneProvider>
  );
}

function DeskScreen({ observedId }: { observedId: string | null }): React.JSX.Element {
  const queryClient = useQueryClient();
  const softphone = useSoftphone();
  const user = useCurrentUser();
  const [activeId, setActiveId] = useState<string | null>(null);
  /**
   * Метки, уже принятые сервером в этой сессии. Снапшот обновляется с задержкой,
   * и без этого списка только что размеченная карточка на секунду возвращалась
   * бы на экран как «незакрытая» — с повторной блокировкой набора.
   */
  const [labeledIds, setLabeledIds] = useState<ReadonlySet<string>>(new Set());
  /**
   * Последнее подтверждение ответа абонента от станции (call.answered, нога
   * транка §7.4). Момент — по часам ЭТОГО браузера на приход события: часы
   * клиента и сервера расходятся сильнее, чем задержка доставки. К звонку
   * привязывается на рендере по назначению или номеру, а сбрасывается при
   * каждом новом наборе — иначе перезвон тому же номеру начал бы «разговор»
   * со старого подтверждения.
   */
  const [serverAnswer, setServerAnswer] = useState<{
    assignmentId: string | null;
    phone: string;
    atClient: number;
  } | null>(null);
  /**
   * Автонабор после метки (просьба диспетчеров, 29.08.2026): поставил
   * результат — пульт сам набирает следующий номер после короткого отсчёта,
   * который видно и можно остановить. Взводится ТОЛЬКО простановкой метки:
   * автонабор при входе на пульт или после F5 означал бы звонок, которого
   * человек не ждёт.
   */
  const [autoDial, setAutoDial] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AUTODIAL_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  /** Момент, когда автонабор должен сработать; null — не взведён */
  const [autoDialDeadline, setAutoDialDeadline] = useState<number | null>(null);

  const snapshotKey = useMemo(() => [...SNAPSHOT_KEY, observedId] as const, [observedId]);
  const snapshotQuery = useQuery({
    queryKey: snapshotKey,
    queryFn: () =>
      api.get<DeskSnapshotResponse>(
        observedId === null ? '/desk/snapshot' : `/desk/snapshot?dispatcherId=${observedId}`,
      ),
  });

  // Метки меняются раз в месяц, а снапшот — на каждое событие: разные запросы,
  // разное время жизни кэша
  const labelsQuery = useQuery({
    queryKey: LABELS_KEY,
    queryFn: () => api.get<LabelsResponse>('/labels'),
    staleTime: 5 * 60_000,
  });

  // Перезвоны: договорённости, ещё не выданные карточками. Живут отдельной
  // ручкой — их нет в снапшоте, но протухают они с ним вместе (reload ниже)
  const callbacksKey = useMemo(() => ['desk', 'callbacks', observedId] as const, [observedId]);
  const callbacksQuery = useQuery({
    queryKey: callbacksKey,
    queryFn: () =>
      api.get<DeskCallbacksResponse>(
        observedId === null ? '/desk/callbacks' : `/desk/callbacks?dispatcherId=${observedId}`,
      ),
  });

  const reload = useCallback((): void => {
    void queryClient.invalidateQueries({ queryKey: snapshotKey });
    void queryClient.invalidateQueries({ queryKey: callbacksKey });
    // Найденный номер тоже: его могли забрать из «Перезвонов» или выдать раздачей
    void queryClient.invalidateQueries({ queryKey: PHONE_LOOKUP_KEY });
  }, [queryClient, snapshotKey, callbacksKey]);

  const onAnswered = useCallback(
    (payload: { assignmentId: string | null; phone: string }): void => {
      // В наблюдении чужой ответ таймеру этого экрана не нужен: звонок идёт
      // в браузере владельца пульта, здесь его панели нет
      if (observedId !== null) return;
      setServerAnswer({
        assignmentId: payload.assignmentId,
        phone: payload.phone,
        atClient: Date.now(),
      });
    },
    [observedId],
  );

  useRealtime(snapshotQuery.data?.seq ?? null, reload, onAnswered, observedId);

  const applyLabel = useMutation({
    mutationFn: (input: { assignmentId: string; body: DeskLabelBody }) =>
      api.post<DeskLabelResponse>(`/desk/assignments/${input.assignmentId}/label`, input.body),
    onSuccess: (_data, input) => {
      setLabeledIds((prev) => new Set(prev).add(input.assignmentId));
      // Метка на сервере — черновик своё отслужил (28.09.2026)
      clearLabelDraft(browserDraftStorage(), user.id, input.assignmentId);
      softphone.clearCall();
      setActiveId(null);
      // Метка поставлена — набор снова открыт; если включён автонабор,
      // взводим отсчёт до следующего номера. В наблюдении не взводим никогда:
      // этот пульт не звонит
      if (autoDial && observedId === null) setAutoDialDeadline(Date.now() + AUTODIAL_DELAY_MS);
      reload();
    },
  });

  /**
   * Готовность живёт на сервере, локальной копии у пульта нет: после ответа
   * перезапрашивается снапшот целиком. Иначе кнопка показывала бы «Готов», а
   * раздача продолжала бы пропускать диспетчера — расхождение, которое человек
   * никак не увидит.
   */
  const setReady = useMutation({
    mutationFn: (ready: boolean) => {
      const body: DeskReadyBody = { ready };
      return api.post<DeskReadyResponse>('/desk/ready', body);
    },
    onSuccess: () => {
      reload();
    },
  });

  /**
   * «Запросить номера»: сигнал администратору, не выдача — авто-раздача
   * добирает сама, кнопка нужна когда добирать НЕЧЕГО (очередь пуста).
   * Время запроса держим локально: подпись «запрошено в 19:05» живёт до F5,
   * а сам запрос на сервере висит, пока админ не догрузит базу.
   */
  const [requestedAt, setRequestedAt] = useState<Date | null>(null);
  const requestContacts = useMutation({
    mutationFn: () => api.post<DeskRequestContactsResponse>('/desk/request-contacts', {}),
    onSuccess: () => {
      setRequestedAt(new Date());
    },
  });

  /**
   * Звонок начинается с намерения: сервер подтверждает владение назначением и
   * выдаёт callTraceId, которым потом склеиваются логи пульта, API и CDR (§7.3).
   * Свой идентификатор браузер придумать не может — он не попадёт в CDR.
   */
  const startCall = useMutation({
    /**
     * Набор закрепляет карточку за экраном.
     *
     * Без этого метка уходила не туда: активной оставалась та карточка, что
     * оказалась первой в списке, а не та, по которой звонили. Стоило
     * диспетчеру переключиться на следующий номер до нажатия метки — и
     * результат разговора приземлялся на чужой контакт. 28.08.2026 за смену
     * так разошлись 18 карточек: размечены без единого звонка, а девять
     * реальных разговоров остались без результата.
     */
    onMutate: (assignment: DeskAssignment) => {
      setActiveId(assignment.id);
    },
    mutationFn: async (assignment: DeskAssignment) => {
      const body: DeskCallIntentBody = { phone: assignment.phone };
      const intent = await api.post<DeskCallIntentResponse>(
        `/desk/assignments/${assignment.id}/call-intent`,
        body,
      );
      await softphone.call({
        phone: assignment.phone,
        assignmentId: assignment.id,
        traceId: intent.callTraceId,
      });
    },
  });

  /**
   * Ручной набор: проверка связи и обратный звонок человеку, которого в очереди нет.
   *
   * Порядок тот же, что у звонка по карточке: сначала сервер, потом софтфон.
   * Сервер проверяет право на исходящий и часовой лимит, выдаёт callTraceId (без него
   * звонок не склеится с CDR) и заголовки INVITE.
   *
   * Набирается номер И заголовок ИЗ ОТВЕТА, а не то, что напечатал человек: диалплан
   * принимает только _+7XXXXXXXXXX и на «8 999…» отвечает отбоем, а признак «карточки
   * нет» — это MANUAL_CALL_MARKER, который назначает сервер. Своя нормализация в этом
   * месте была бы вторым ответом на вопрос, какой номер уехал в диалплан.
   *
   * «Свой номер» (24.09.2026): на номер не из сегодняшней базы сервер выдаёт
   * КАРТОЧКУ. Она кладётся в снапшот, становится активной и набирается тем же
   * startCall, что и кнопка «Позвонить», — как перезвон из блока «Перезвоны».
   * Иначе звонок ушёл бы без карточки, и метку результата ставить было бы некуда.
   */
  const manualCall = useMutation({
    mutationFn: async (input: string) => {
      const body: DeskManualCallBody = { phone: input };
      const intent = await api.post<DeskManualCallResponse>('/desk/manual-call', body);
      if (intent.kind === 'manual') {
        await softphone.call({
          phone: intent.phone,
          assignmentId: intent.sipHeaders['X-Assignment-Id'],
          traceId: intent.callTraceId,
        });
      }
      return intent;
    },
    onSuccess: (intent) => {
      if (intent.kind !== 'card') return;
      const card = intent.assignment;
      queryClient.setQueryData<DeskSnapshotResponse>(snapshotKey, (prev) =>
        prev === undefined || prev.assignments.some((a) => a.id === card.id)
          ? prev
          : { ...prev, assignments: [card, ...prev.assignments] },
      );
      setActiveId(card.id);
      reload();
      // manualCall.isPending здесь ещё true — поэтому не anyCallBusy, а только startCall
      if (softphone.canCall && !startCall.isPending && dialAllowed(card.id, unlabeled)) {
        startCall.mutate(card);
      }
    },
  });

  // Пустой массив по умолчанию создаётся заново на каждый рендер и сбрасывал бы
  // все useMemo ниже — а вместе с ними и список карточек под курсором диспетчера
  const assignments = useMemo(() => snapshotQuery.data?.assignments ?? [], [snapshotQuery.data]);
  const openAssignments = useMemo(
    () => assignments.filter((a) => a.status !== 'labeled' && a.status !== 'revoked'),
    [assignments],
  );
  /**
   * Открытые карточки БЕЗ только что размеченных: снапшот едет с задержкой,
   * и без фильтра свежезакрытая карточка на секунду оставалась бы «следующей» —
   * а автонабор успел бы позвонить по ней и получить отказ сервера.
   *
   * Разговоры без метки — наверху, самый свежий первым (28.09.2026): после F5
   * активной становится именно такая карточка, а не одна из 40 (orderForDesk).
   */
  const visibleOpen = useMemo(
    () => orderForDesk(openAssignments.filter((a) => !labeledIds.has(a.id)), labeledIds),
    [openAssignments, labeledIds],
  );

  /**
   * Уборка черновиков меток (28.09.2026): карточку, которой сервер больше не
   * держит открытой, уже не разметить — её черновик удаляется; старше суток —
   * тоже. Только по свежему снапшоту: кэш с прошлого захода на пульт может не
   * знать карточку, выданную позже, и снёс бы её черновик. В наблюдении
   * черновиков нет вовсе (см. LabelPicker ниже).
   */
  const snapshotFetching = snapshotQuery.isFetching;
  useEffect(() => {
    if (observedId !== null || snapshotQuery.data === undefined || snapshotFetching) return;
    purgeLabelDrafts(
      browserDraftStorage(),
      user.id,
      new Set(openAssignments.map((a) => a.id)),
      Date.now(),
    );
  }, [observedId, snapshotQuery.data, snapshotFetching, openAssignments, user.id]);

  // Пришли карточки — сигнал «дайте номера» отработан: плашка «запрошено»
  // уступает место кнопке на СЛЕДУЮЩЕЕ опустошение, иначе висела бы навсегда
  useEffect(() => {
    if (visibleOpen.length > 0) setRequestedAt(null);
  }, [visibleOpen.length]);

  const active = useMemo(
    () => visibleOpen.find((a) => a.id === activeId) ?? visibleOpen[0] ?? null,
    [visibleOpen, activeId],
  );

  const { snapshot } = softphone;
  const line = snapshotQuery.data?.line ?? null;

  // Бип «вы в эфире» — один раз на каждое карусельное соединение (§7.11):
  // абонент уже на линии, и здороваться надо сразу, не глядя на экран
  const carouselCallId = snapshot.call?.isCarousel === true ? snapshot.call.id : null;
  useEffect(() => {
    if (carouselCallId !== null && observedId === null) playConnectBeep();
  }, [carouselCallId, observedId]);
  const blockedReason = line?.blockedReason ?? null;

  /**
   * Ручной звонок висит на пульте (идёт или завершён и ещё не убран).
   *
   * Пока он висит, метки результата не показываются: они относятся к контакту из
   * очереди, и поставленная после проверки связи метка закроет живую карточку
   * чужим результатом — контакт уйдёт из прозвона, и никто этого не заметит.
   */
  const manualCallOnAir = isManualCall(snapshot.call);
  const cardCall = snapshot.call !== null && !manualCallOnAir ? snapshot.call : null;
  const cardCallEnded = cardCall !== null && cardCall.state === 'ended';

  /**
   * Разговоры без метки — с ними работа стоит. Считаются по упорядоченному
   * списку: первым идёт самый свежий разговор, и результат ставится ему же,
   * кого пульт показал активным (28.09.2026)
   */
  const unlabeled = useMemo(
    () => unlabeledAssignments(visibleOpen, labeledIds),
    [visibleOpen, labeledIds],
  );
  const unlabeledIds = useMemo(() => new Set(unlabeled.map((a) => a.id)), [unlabeled]);

  /** Карточка, к которой относится текущий звонок */
  const callAssignment = useMemo(
    () =>
      cardCall === null
        ? null
        : (openAssignments.find((a) => a.id === cardCall.assignmentId) ?? null),
    [cardCall, openAssignments],
  );

  /** Кому сейчас ставится результат — правило в labelTargetFor */
  const resultTarget = labelTargetFor(callAssignment, active, unlabeled);
  /**
   * На экране карточка, по которой разговор уже был, а метки нет (ревью
   * 28.09.2026): после F5 это первая карточка списка. Звать её «следующим
   * звонком» с синей «Позвонить» нельзя — рука набирает клиента повторно
   * вместо метки. Перезвон после обрыва остаётся кнопкой «Перезвонить».
   */
  const awaitingLabel = cardCall === null && active !== null && unlabeledIds.has(active.id);
  /** Результат обязателен: звонок завершён или висит незакрытый разговор */
  const resultDemanding =
    (cardCallEnded && callAssignment !== null) || (cardCall === null && unlabeled.length > 0);

  /**
   * Завершённый звонок по карточке, которой больше нет (размечена в другой
   * вкладке, отозвана админом) — убирается сам: метку ставить уже некуда,
   * а панель звонка без неё висела бы вечно.
   */
  const { clearCall } = softphone;
  useEffect(() => {
    if (cardCallEnded && callAssignment === null) clearCall();
  }, [cardCallEnded, callAssignment, clearCall]);

  // Каждый новый звонок начинается без подтверждения ответа: id сессии софтфона
  // меняется на каждый набор, и старое подтверждение по тому же номеру сгорает
  const callSessionId = snapshot.call?.id ?? null;
  useEffect(() => {
    setServerAnswer(null);
  }, [callSessionId]);

  /**
   * Подтверждённый станцией ответ для ТЕКУЩЕГО звонка. Карточный звонок
   * сверяется по назначению, ручной — по номеру (назначения у него нет, §7.3).
   */
  const answeredAtClient = useMemo(() => {
    const call = snapshot.call;
    if (call === null) return null;
    // Карусель соединяет пульт с уже ответившим абонентом: call.answered
    // приходит раньше, чем звонок появляется в софтфоне, и сброс на новом
    // звонке его стирает — таймер стоял на «—:—» (11.09.2026, Катя: «не идут
    // секунды»). Разговор здесь начинается, когда пульт снял трубку
    if (call.isCarousel) return call.answeredAt;
    if (serverAnswer === null) return null;
    const matches =
      serverAnswer.assignmentId !== null
        ? serverAnswer.assignmentId === call.assignmentId
        : serverAnswer.phone === call.phone;
    return matches ? serverAnswer.atClient : null;
  }, [snapshot.call, serverAnswer]);

  // «Пик» в наушнике, когда человек снял трубку (14.09.2026): гудок теперь
  // тихий, и без сигнала момент ответа легко пропустить. У карусели свой
  // сигнал — при появлении звонка, повторять его здесь незачем
  const answeredCallId =
    answeredAtClient !== null && snapshot.call !== null && !snapshot.call.isCarousel
      ? snapshot.call.id
      : null;
  useEffect(() => {
    if (answeredCallId !== null && observedId === null) playConnectBeep();
  }, [answeredCallId, observedId]);

  // Громкость гудков и голоса клиента — своя у каждого диспетчера (14.09.2026).
  // Поток со станции один, поэтому громкость переключается в момент ответа
  const [volume, setVolume] = useState<CallVolume>(() => readVolume());
  const hasCall = snapshot.call !== null && snapshot.call.state !== 'ended';
  const answered = answeredAtClient !== null;
  useEffect(() => {
    if (observedId !== null) return;
    applyVolume(volumeForPhase({ hasCall, answered }, volume));
  }, [hasCall, answered, volume, observedId]);

  const anyCallBusy = startCall.isPending || manualCall.isPending;
  const canDialActive =
    softphone.canCall &&
    !anyCallBusy &&
    active !== null &&
    dialAllowed(active.id, unlabeled);
  /**
   * Автонабор — только когда разговоров без метки нет (28.09.2026). Такая
   * карточка теперь стоит первой и становится активной, а dialAllowed свою
   * незакрытую набирать разрешает (перезвон после обрыва). Без этой проверки
   * автонабор после метки одной карточки сам позвонил бы клиенту другой, чей
   * разговор ещё без результата. Перезвонить ему можно — но только руками.
   */
  const autoDialReady = canDialActive && unlabeled.length === 0;
  /** Причина, по которой кнопка набора закрыта — говорится словами, а не серой кнопкой */
  const dialBlockedByLabel =
    active !== null && unlabeled.length > 0 && !dialAllowed(active.id, unlabeled);

  /**
   * Перезвон по клику из блока «Перезвоны» (06.09.2026). Сервер выдаёт карточку
   * сразу — это свой перезвон, а не выдача из общего пула (ADR-0006, уточнение).
   * Карточка кладётся в снапшот, не дожидаясь события, становится активной и
   * набирается тем же путём, что и кнопка «Позвонить». Если набор сейчас закрыт
   * (незакрытый разговор, линия) — карточка просто становится активной, и
   * диспетчер видит на ней, почему звонок не пошёл.
   */
  const takeCallback = useMutation({
    mutationFn: (queueItemId: string) =>
      api.post<DeskCallbackTakeResponse>(`/desk/callbacks/${queueItemId}/take`, {}),
    onSuccess: (data) => {
      const card = data.assignment;
      queryClient.setQueryData<DeskSnapshotResponse>(snapshotKey, (prev) =>
        prev === undefined || prev.assignments.some((a) => a.id === card.id)
          ? prev
          : { ...prev, assignments: [card, ...prev.assignments] },
      );
      queryClient.setQueryData<DeskCallbacksResponse>(callbacksKey, (prev) =>
        prev === undefined
          ? prev
          : { items: prev.items.filter((item) => item.queueItemId !== card.queueItemId) },
      );
      setActiveId(card.id);
      reload();
      if (softphone.canCall && !anyCallBusy && dialAllowed(card.id, unlabeled)) {
        startCall.mutate(card);
      }
    },
  });

  /**
   * Срабатывание автонабора. Таймер живёт, пока условия набора выполняются;
   * рухнули (второй незакрытый разговор, линия упала) — cleanup снимает его,
   * дозрели обратно — эффект ставит заново на остаток отсчёта.
   */
  const startCallMutate = startCall.mutate;
  useEffect(() => {
    if (autoDialDeadline === null) return;
    if (!autoDial) {
      setAutoDialDeadline(null);
      return;
    }
    // Набор так и не открылся (карточки кончились, линия) — взвод протухает:
    // звонок, стартующий сам спустя минуты тишины, диспетчер уже не ждёт
    if (Date.now() > autoDialDeadline + AUTODIAL_STALE_MS) {
      setAutoDialDeadline(null);
      return;
    }
    // canDialActive включает active !== null — TS сужает active через алиас
    if (!canDialActive) return;
    if (!autoDialReady) return;

    const target = active;
    const id = setTimeout(
      () => {
        setAutoDialDeadline(null);
        startCallMutate(target);
      },
      Math.max(0, autoDialDeadline - Date.now()),
    );
    return () => {
      clearTimeout(id);
    };
  }, [autoDialDeadline, autoDial, canDialActive, autoDialReady, active, startCallMutate]);

  return (
    <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-4 p-4 lg:px-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        {observedId === null ? (
          <LineIndicator line={snapshot.line} isLeaderTab={snapshot.isLeaderTab} />
        ) : null}
        {user.role !== 'dispatcher' ? <DeskSwitcher observedId={observedId} /> : null}
        <DayStats
          stats={snapshotQuery.data?.stats ?? null}
          usedToday={line?.usedToday ?? null}
          remaining={snapshotQuery.data === undefined ? null : visibleOpen.length}
        />
        {observedId === null ? (
          <DevicePicker
            inputId={snapshot.devices.inputId}
            outputId={snapshot.devices.outputId}
            onSelectInput={(id) => {
              void softphone.setInputDevice(id);
            }}
            onSelectOutput={(id) => {
              void softphone.setOutputDevice(id);
            }}
            volume={volume}
            onVolumeChange={(next) => {
              writeVolume(next);
              setVolume(next);
            }}
          />
        ) : null}
      </header>

      {/* Пока снапшот не пришёл, готовность не рисуется вовсе: «Готовность выключена»
          по отсутствующим данным — это ложь, по которой диспетчер пойдёт жать кнопку */}
      {line === null ? (
        <ErrorNotice
          error={snapshotQuery.error}
          action="Состояние пульта не загрузилось. Готовность и причина простоя не показаны — это не «всё в порядке». Обновите страницу."
        />
      ) : (
        <ReadyToggle
          ready={line.ready}
          sipRegistered={line.sipRegistered}
          blockedReason={blockedReason}
          canToggle={user.role === 'dispatcher'}
          busy={setReady.isPending}
          error={errorText(setReady.error)}
          onToggle={(next) => {
            setReady.mutate(next);
          }}
          openNow={line.openNow}
          maxOpen={line.maxOpen}
          usedToday={line.usedToday}
          dailyLimit={line.dailyLimit}
        />
      )}

      {/* Тумблер автонабора живёт ЗДЕСЬ, а не в панели набора (10.09.2026).
          Набор после метки стартует мгновенно, панель набора тут же сменяется
          панелью звонка — и выключить автонабор было физически некогда:
          «доля секунды, никак не успеть нажать». Сверху он виден всегда,
          в том числе во время разговора */}
      {observedId === null ? (
        <AutoDialToggle
          autoDial={autoDial}
          onToggle={() => {
            setAutoDial((prev) => {
              const next = !prev;
              try {
                localStorage.setItem(AUTODIAL_STORAGE_KEY, next ? '1' : '0');
              } catch {
                // хранилище недоступно — выбор просто не переживёт F5
              }
              if (!next) setAutoDialDeadline(null);
              return next;
            });
          }}
        />
      ) : null}

      {/* Несколько незакрытых разговоров разом — например, после возвращения на
          пульт. Один незакрытый и так подсвечен блоком результата */}
      {unlabeled.length > 1 ? (
        <NeedsLabel
          assignments={unlabeled}
          onOpen={(assignment) => {
            setActiveId(assignment.id);
          }}
        />
      ) : null}

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(300px,360px)_minmax(0,1fr)_minmax(280px,340px)]">
        {/* ЛЕВАЯ КОЛОНКА: кому звоним и служебные инструменты */}
        <div className="flex min-w-0 flex-col gap-4">
          {active !== null ? (
            <ContactCard assignment={active} />
          ) : (
            <p className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-500">
              Контактов нет. Раздачу делает администратор.
              {blockedReason !== null
                ? ` Сервер вас пропускает: ${blockedReasonTitle(blockedReason)}.`
                : ' Если пульт пуст надолго — проверьте линию и кнопку «Готов».'}
            </p>
          )}

          {/* Ручной набор и проверка звука — только на своём пульте:
              в наблюдении звонить и слушать микрофон нечем и незачем */}
          {observedId === null ? (
            <ManualDialForm
              canCall={softphone.canCall && !anyCallBusy && unlabeled.length === 0}
              busy={manualCall.isPending}
              error={manualCallErrorText(manualCall.error)}
              remainingThisHour={
                manualCall.data?.kind === 'manual' ? manualCall.data.remainingThisHour : null
              }
              ownNumbers={
                manualCall.data?.kind === 'card'
                  ? { left: manualCall.data.ownNumbersLeftToday, perDay: manualCall.data.ownNumbersPerDay }
                  : null
              }
              onCall={(input) => {
                manualCall.mutate(input);
              }}
            />
          ) : null}

          {/* Проверка звука — в начале смены, а не когда клиент уже на линии.
              Во время разговора прячется: микрофон занят, вторая запись его отберёт */}
          {observedId === null && snapshot.call === null ? (
            <AudioCheck
              inputDeviceId={snapshot.devices.inputId ?? null}
              outputDeviceId={snapshot.devices.outputId ?? null}
            />
          ) : null}
        </div>

        {/* ЦЕНТР: звонок и его результат */}
        <div className="flex min-w-0 flex-col gap-4">
          {snapshot.call !== null ? (
            <CallPanel
              call={snapshot.call}
              contactName={callAssignment?.fullName ?? null}
              answeredAtClient={answeredAtClient}
              onHangup={() => {
                void softphone.hangup();
              }}
              onToggleMute={softphone.setMuted}
              {...(manualCallOnAir ? { onDismiss: softphone.clearCall } : {})}
            />
          ) : (
            <DialCard
              assignment={active}
              observing={observedId !== null}
              carouselActive={line?.carouselActive ?? false}
              canCall={canDialActive}
              lineState={snapshot.line}
              isLeaderTab={snapshot.isLeaderTab}
              busy={startCall.isPending}
              blockedByLabel={dialBlockedByLabel}
              awaitingLabel={awaitingLabel}
              error={errorText(startCall.error)}
              onCall={(assignment) => {
                startCall.mutate(assignment);
              }}
              autoDialDeadline={autoDialReady ? autoDialDeadline : null}
              onCancelAutoDial={() => {
                setAutoDialDeadline(null);
              }}
              requestSlot={
                line?.ready !== true || !line.sipRegistered ? undefined : requestedAt !== null ? (
                  <p className="rounded-xl bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
                    Запрошено в {formatTimeMsk(requestedAt)} — администратор видит ваш запрос.
                    Номера придут сами, как только появятся.
                  </p>
                ) : (
                  <Button
                    variant="secondary"
                    busy={requestContacts.isPending}
                    onClick={() => {
                      requestContacts.mutate();
                    }}
                  >
                    Запросить номера у администратора
                  </Button>
                )
              }
            />
          )}

          {manualCallOnAir ? (
            <p className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-600">
              Идёт ручной набор — метки скрыты. Метка закрывает контакт из очереди,
              а у этого звонка карточки нет. Закройте звонок, чтобы вернуться к карточке.
            </p>
          ) : resultTarget !== null ? (
            /* key по назначению: смена карточки (разметили в другой вкладке,
               отозвал админ, переключение пульта) пересоздаёт панель с чистым
               состоянием — иначе заполненная анкета уехала бы на чужой номер */
            <LabelPicker
              key={resultTarget.id}
              phone={formatPhone(resultTarget.phone)}
              labels={labelsQuery.data?.items ?? []}
              busy={applyLabel.isPending}
              demanding={resultDemanding}
              callInProgress={cardCall !== null && !cardCallEnded}
              error={errorText(applyLabel.error)}
              onPick={(body) => {
                applyLabel.mutate({ assignmentId: resultTarget.id, body });
              }}
              /* Черновик — только на своём пульте (28.09.2026). В наблюдении
                 админ заполняет форму в своём браузере за чужую карточку:
                 хранить это незачем, а оставленный черновик потом всплыл бы
                 админу на карточке, которую диспетчер давно закрыл сам */
              draftOwner={
                observedId === null ? { userId: user.id, assignmentId: resultTarget.id } : undefined
              }
              {...(resultDemanding && !anyCallBusy && softphone.canCall
                ? {
                    onRedial: () => {
                      startCall.mutate(resultTarget);
                    },
                  }
                : {})}
            />
          ) : null}
        </div>

        {/* ПРАВАЯ КОЛОНКА: перезвоны и остальные карточки диспетчера */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* «Найти номер» (24.09.2026) — рядом с перезвонами: ищут чаще всего
              перезвон, который пропал из блока. Свой перезвон забирается тем
              же takeCallback; после попытки найденное перечитывается */}
          <PhoneLookup
            observedId={observedId}
            {...(observedId === null
              ? {
                  onTake: (queueItemId: string) => {
                    takeCallback.mutate(queueItemId, {
                      onSettled: () => {
                        void queryClient.invalidateQueries({ queryKey: PHONE_LOOKUP_KEY });
                      },
                    });
                  },
                  takingId: takeCallback.isPending ? takeCallback.variables : null,
                  takeError: errorText(takeCallback.error),
                  // Ошибка одна на пульт: показываем её только у того номера, который забирали
                  takeErrorId: takeCallback.isError ? takeCallback.variables : null,
                }
              : {})}
          />
          {/* Ошибка загрузки перезвонов не молчит: пустой блок и «перезвонов
              нет» — разные вещи, а напоминание и есть назначение блока */}
          {callbacksQuery.isError ? (
            <ErrorNotice
              error={callbacksQuery.error}
              action="Перезвоны не загрузились — напоминаний не будет, хотя договорённости могут быть. Номера всё равно придут карточками. Обновите страницу."
            />
          ) : (
            <CallbacksPanel
              items={callbacksQuery.data?.items ?? []}
              withSound={observedId === null}
              {...(observedId === null
                ? {
                    onTake: (item: DeskCallbacksResponse['items'][number]) => {
                      takeCallback.mutate(item.queueItemId);
                    },
                    takingId: takeCallback.isPending ? takeCallback.variables : null,
                    takeError: errorText(takeCallback.error),
                  }
                : {})}
            />
          )}
          <MyQueue
            assignments={visibleOpen}
            needsLabelIds={unlabeledIds}
            activeId={active?.id ?? null}
            onSelect={(assignment) => {
              setActiveId(assignment.id);
            }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Переключатель пультов для админа и супервизора: свой ↔ пульт диспетчера.
 *
 * Живёт в адресной строке (?dispatcher=…), а не в состоянии: ссылку на пульт
 * конкретного диспетчера можно открыть с «Обзора» и переслать. Список — только
 * активные диспетчеры; точка рядом с именем показывает, кто сейчас на линии.
 */
function DeskSwitcher({ observedId }: { observedId: string | null }): React.JSX.Element {
  const navigate = useNavigate();
  const dispatchersQuery = useQuery({
    queryKey: ['users', 'dispatchers-for-desk'],
    queryFn: () => api.get<{ items: UserRow[] }>('/users?role=dispatcher&isActive=true'),
    staleTime: 60_000,
  });

  const items = dispatchersQuery.data?.items ?? [];
  const observed = items.find((u) => u.id === observedId) ?? null;

  return (
    <div className="flex items-center gap-2">
      <span
        className={
          'flex h-8 w-8 items-center justify-center rounded-lg ' +
          (observedId === null ? 'bg-slate-100 text-slate-400' : 'bg-blue-600 text-white')
        }
      >
        <Eye className="h-4 w-4" />
      </span>
      <select
        value={observedId ?? ''}
        onChange={(event) => {
          const next = event.target.value;
          void navigate({
            to: '/desk',
            search: next === '' ? {} : { dispatcher: next },
          });
        }}
        className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-800"
      >
        <option value="">Мой пульт</option>
        {items.map((u) => (
          <option key={u.id} value={u.id}>
            Пульт: {u.fullName}
            {u.ready && u.sipRegistered ? ' · на линии' : ''}
          </option>
        ))}
      </select>
      {observed !== null ? (
        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
          наблюдение
        </span>
      ) : null}
    </div>
  );
}

/**
 * Тумблер автонабора. Стоит наверху страницы и виден всегда, в том числе во
 * время разговора: набор после метки уходит мгновенно, и внутри панели набора
 * выключатель было не поймать — «доля секунды, никак не успеть» (10.09.2026).
 */
function AutoDialToggle({
  autoDial,
  onToggle,
}: {
  autoDial: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onToggle}
        className={
          'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ' +
          (autoDial
            ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
            : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50')
        }
      >
        <span
          className={'h-2.5 w-2.5 rounded-full ' + (autoDial ? 'bg-blue-600' : 'bg-slate-300')}
          aria-hidden
        />
        Автонабор после метки: {autoDial ? 'включён' : 'выключен'}
      </button>
      {autoDial ? (
        <span className="text-xs text-slate-500">
          следующий номер набирается сразу после метки — выключить можно прямо во время разговора
        </span>
      ) : null}
    </div>
  );
}

/**
 * Почему набор закрыт и что нажать, чтобы он открылся (10.09.2026).
 *
 * Серая кнопка без объяснения — это звонок администратору: у новых диспетчеров
 * 10.09 пульт «не звонил», а причина была не видна на экране. Обе частые беды
 * чинятся отсюда: линию можно забрать из другой вкладки и поднять заново.
 */
function LineTrouble({
  lineState,
  isLeaderTab,
}: {
  lineState: SoftphoneSnapshot['line'];
  isLeaderTab: boolean;
}): React.JSX.Element {
  const { takeLine, reconnect } = useLineRepair();

  if (!isLeaderTab) {
    return (
      <div className="flex w-full max-w-sm flex-col items-center gap-2 rounded-xl bg-amber-50 px-4 py-3">
        <p className="text-sm font-medium text-amber-900">
          Линию держит другая вкладка кабинета — здесь набор закрыт.
        </p>
        <Button variant="primary" size="lg" className="w-full" onClick={takeLine}>
          Работать в этой вкладке
        </Button>
      </div>
    );
  }

  if (lineState !== 'registered') {
    return (
      <div className="flex w-full max-w-sm flex-col items-center gap-2 rounded-xl bg-amber-50 px-4 py-3">
        <p className="text-sm font-medium text-amber-900">
          Нет связи со станцией. Проверьте интернет и разрешите микрофон, потом поднимите
          линию заново.
        </p>
        <Button variant="primary" size="lg" className="w-full" onClick={reconnect}>
          Переподключить линию
        </Button>
      </div>
    );
  }

  return (
    <p className="text-sm text-slate-500">
      Набор откроется, как только освободится линия. Если не открылся — переподключите её.
    </p>
  );
}

/**
 * Центральная панель, когда звонка нет: следующий номер и одна кнопка набора.
 *
 * Кнопка живёт здесь, а не в карточке контакта: действие на пульте одно,
 * и оно всегда в одном и том же месте — там же, где потом появится звонок.
 *
 * Тумблер автонабора вынесен наверх страницы: набор стартует сразу после
 * метки, и панель набора успевает смениться панелью звонка — выключатель в
 * ней недосягаем (10.09.2026).
 */
function DialCard({
  assignment,
  observing,
  canCall,
  busy,
  blockedByLabel,
  awaitingLabel,
  lineState,
  isLeaderTab,
  error,
  onCall,
  autoDialDeadline,
  onCancelAutoDial,
  requestSlot,
  carouselActive,
}: {
  assignment: DeskAssignment | null;
  /** Чужой пульт на экране: звонить нечем, панель только показывает очередь */
  observing: boolean;
  canCall: boolean;
  busy: boolean;
  /** Набор закрыт правилом «сначала метка по прошлому разговору» */
  blockedByLabel: boolean;
  /** На экране разговор без метки: не «следующий звонок», а «поставьте метку» */
  awaitingLabel: boolean;
  /** Состояние линии в ЭТОЙ вкладке: по нему объясняем, почему набор закрыт */
  lineState: SoftphoneSnapshot['line'];
  isLeaderTab: boolean;
  error: string | undefined;
  onCall: (assignment: DeskAssignment) => void;
  /** Кнопка «Запросить номера»: живёт в родителе, здесь только место в пустом экране */
  requestSlot?: React.ReactNode;
  /** Момент автонабора; null — отсчёт не идёт */
  autoDialDeadline: number | null;
  onCancelAutoDial: () => void;
  /** Карусель гудит за диспетчера (§7.11): вместо «Позвонить» — режим ожидания соединения */
  carouselActive: boolean;
}): React.JSX.Element {
  const autoDialLeftSec = useCountdownSec(autoDialDeadline);
  const soundReady = useSoundUnlocked();

  // Автодозвон: станция набирает сама, кнопка «Позвонить» и автонабор не нужны —
  // диспетчер должен понимать, что тишина в наушнике это ОЖИДАНИЕ, а не поломка
  if (carouselActive && !observing) {
    // После перезагрузки браузер держит звук выключенным до первого клика,
    // и пульт вынужден отбивать соединения (боевой случай 01.09: все вызовы
    // уходили в 486). Пока клика не было — просим его большой кнопкой
    if (!soundReady) {
      return (
        <section className="flex flex-col items-center gap-4 rounded-2xl border border-amber-300 bg-amber-50 px-6 py-10 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <Phone className="h-6 w-6" />
          </span>
          <p className="text-lg font-bold text-amber-900">Нажмите, чтобы включить звук</p>
          <p className="max-w-sm text-sm text-amber-800">
            После обновления страницы браузер держит звук выключенным. Пока он выключен,
            автодозвон не сможет соединять вас с ответившими.
          </p>
          <button
            type="button"
            className="rounded-xl bg-amber-600 px-6 py-3 text-base font-semibold text-white hover:bg-amber-700"
          >
            Включить звук и работать
          </button>
        </section>
      );
    }
    return (
      <section className="flex flex-col items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50/60 px-6 py-10 text-center">
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <Phone className="h-6 w-6" />
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-300 opacity-40" aria-hidden />
        </span>
        <p className="text-lg font-bold text-emerald-900">Автодозвон включён</p>
        <p className="max-w-sm text-sm text-emerald-800">
          Станция набирает номера за вас. Когда человек снимет трубку, вы услышите
          сигнал — и разговор появится здесь сам. Звонить вручную не нужно.
        </p>
      </section>
    );
  }

  if (assignment === null) {
    return (
      <section className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <Phone className="h-6 w-6" />
        </span>
        <p className="text-lg font-bold text-slate-900">
          {observing ? 'У диспетчера нет открытых карточек' : 'Звонить пока некому'}
        </p>
        <p className="text-sm text-slate-500">Как только придёт контакт, он появится здесь.</p>
        {observing ? null : requestSlot}
      </section>
    );
  }

  return (
    <section className="flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center">
      {awaitingLabel ? (
        <p className="text-sm font-semibold text-amber-700">Разговор был — поставьте метку ниже</p>
      ) : (
        <p className="text-sm font-semibold tracking-wide text-slate-500 uppercase">
          Следующий звонок
        </p>
      )}
      <div className="flex flex-col gap-1">
        <p className="text-3xl font-extrabold tabular-nums tracking-tight text-slate-900">
          {formatPhone(assignment.phone)}
        </p>
        {assignment.fullName !== null ? (
          <p className="text-base text-slate-500">{assignment.fullName}</p>
        ) : null}
      </div>

      {observing ? (
        <p className="rounded-xl bg-slate-50 px-4 py-2.5 text-sm text-slate-600">
          Наблюдение: звонит диспетчер со своего пульта. Метку поставить можно.
        </p>
      ) : autoDialDeadline !== null && autoDialLeftSec > 0 ? (
        <div className="flex w-full max-w-sm flex-col items-center gap-2">
          <p className="text-base font-semibold text-blue-700">
            Наберу сам через {autoDialLeftSec}…
          </p>
          <Button variant="secondary" size="lg" className="w-full" onClick={onCancelAutoDial}>
            Стоп — не звонить
          </Button>
        </div>
      ) : awaitingLabel ? null : (
        <Button
          variant="primary"
          size="lg"
          disabled={!canCall}
          busy={busy}
          onClick={() => {
            onCall(assignment);
          }}
          className="w-full max-w-sm"
        >
          <Phone className="h-5 w-5" />
          Позвонить
        </Button>
      )}

      {blockedByLabel ? (
        <p className="text-sm font-medium text-amber-700">
          Сначала поставьте метку по прошлому разговору — потом откроется набор.
        </p>
      ) : null}

      {/* Кнопка серая — говорим почему и даём починить прямо здесь (10.09.2026):
          диспетчер видел неактивный набор без единого слова и звал администратора */}
      {!observing && !canCall && !busy && !blockedByLabel && !awaitingLabel ? <LineTrouble
        lineState={lineState}
        isLeaderTab={isLeaderTab}
      /> : null}

      {error !== undefined ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Разрешён ли звук браузером: до первого клика после загрузки — нет, и
 * авто-ответ карусели вынужден отбивать соединения. Первый же клик или
 * клавиша снимает запрет навсегда (sticky activation).
 */
function useSoundUnlocked(): boolean {
  const [unlocked, setUnlocked] = useState<boolean>(() => {
    const activation = (navigator as { userActivation?: { hasBeenActive: boolean } }).userActivation;
    return activation === undefined ? true : activation.hasBeenActive;
  });

  useEffect(() => {
    if (unlocked) return;
    const onGesture = (): void => {
      setUnlocked(true);
    };
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('keydown', onGesture, { once: true });
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, [unlocked]);

  return unlocked;
}

/** Целые секунды до дедлайна, обновление четырежды в секунду — отсчёт не дёргается */
function useCountdownSec(deadline: number | null): number {
  const [leftSec, setLeftSec] = useState(0);

  useEffect(() => {
    if (deadline === null) {
      setLeftSec(0);
      return;
    }
    const tick = (): void => {
      setLeftSec(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => {
      clearInterval(id);
    };
  }, [deadline]);

  return leftSec;
}

/**
 * Поток изменений с сервера. Почти любое событие и любой рассинхрон приводят
 * к одному действию — перезапросу снапшота: применять дельты руками значит
 * завести второй способ получить то же состояние.
 *
 * Исключение одно: гудки и ответ абонента (call.ringing / call.answered).
 * Карточек они не меняют — гонять снапшот на каждое такое событие незачем,
 * а вот момент ответа пульту нужен сам по себе: с него стартует таймер
 * разговора (§7.4, ответ считается по ноге транка).
 *
 * Комнаты не перечисляются: пустая заявка означает «выдай положенные по роли»,
 * и сервер подписывает диспетчера на user:<id> и dispatcher:<id>. Раньше пульт
 * просил queue:global, которую диспетчеру не выдают, — и не получал ни одного
 * события: новые карточки появлялись только после F5.
 */
function useRealtime(
  baselineSeq: number | null,
  reload: () => void,
  onAnswered: (payload: { assignmentId: string | null; phone: string }) => void,
  /** Чей пульт наблюдаем; null — свой (комнаты выдаст сервер по роли) */
  observedId: string | null,
): void {
  // Через ref: сокет живёт до конца смены, а колбэки пересоздаются рендерами
  const handlers = useRef({ reload, onAnswered });
  handlers.current = { reload, onAnswered };

  useEffect(() => {
    if (baselineSeq === null) return;
    const client = new RealtimeClient({
      // В наблюдении подписка — на комнаты ДИСПЕТЧЕРА: его метки и звонки
      // должны обновлять этот экран; право админа на чужие комнаты решает сервер
      rooms: observedId === null ? [] : [`user:${observedId}`, `dispatcher:${observedId}`],
      onReady: () => {
        handlers.current.reload();
      },
      onEvent: (event) => {
        if (event.type === 'call.ringing') return;
        if (event.type === 'call.answered') {
          const p = event.payload as { assignmentId?: string | null; phone?: string };
          if (typeof p.phone === 'string') {
            handlers.current.onAnswered({ assignmentId: p.assignmentId ?? null, phone: p.phone });
          }
          return;
        }
        handlers.current.reload();
      },
      onDesync: () => {
        handlers.current.reload();
      },
    });
    client.setBaseline(baselineSeq);
    client.connect();
    return () => {
      client.close();
    };
    // Пересоздаём при первом снимке и при смене наблюдаемого пульта:
    // baselineSeq дальше двигает сам клиент
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineSeq === null, observedId]);
}
